// Capa de dibujo del bloc de To Do's: dos lienzos (el resaltador va debajo del texto y no lo tapa; marcador y figuras, encima) y una
// capa que recibe el dedo, el mouse o el lápiz solo cuando hay una herramienta de dibujo elegida. Los trazos son vectores (ver logica/trazos.ts).
import { useEffect, useRef, useState } from 'preact/hooks';
import { esToque, simplificar, trazoEn, type Figura, type Punto, type Trazo } from '../logica/trazos';

export type Herramienta = 'escribir' | 'marcador' | 'resaltador' | 'figura' | 'borrador';
export interface Estilo { color: string; tam: number }

function trazarFigura(g: CanvasRenderingContext2D, t: Extract<Trazo, { t: 'figura' }>, ancho: number) {
  const ax = t.a[0] * ancho, ay = t.a[1], bx = t.b[0] * ancho, by = t.b[1];
  const x = Math.min(ax, bx), y = Math.min(ay, by), w = Math.abs(bx - ax), h = Math.abs(by - ay);
  g.lineCap = 'round'; g.lineJoin = 'round'; g.strokeStyle = t.color; g.lineWidth = t.tam; g.beginPath();
  const f: Figura = t.f;
  if (f === 'rect') g.rect(x, y, w, h);
  else if (f === 'ovalo') g.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, 6.283);
  else if (f === 'linea') { g.moveTo(ax, ay); g.lineTo(bx, by); }
  else if (f === 'flecha') {
    const ang = Math.atan2(by - ay, bx - ax), L = Math.max(12, t.tam * 3.2);
    g.moveTo(ax, ay); g.lineTo(bx, by);
    g.moveTo(bx - L * Math.cos(ang - 0.45), by - L * Math.sin(ang - 0.45)); g.lineTo(bx, by); g.lineTo(bx - L * Math.cos(ang + 0.45), by - L * Math.sin(ang + 0.45));
  } else if (f === 'estrella') {
    const cx = x + w / 2, cy = y + h / 2;
    for (let k = 0; k < 10; k++) { const rr = k % 2 ? 0.42 : 1, an = -1.5708 + k * 0.62832, px = cx + Math.cos(an) * rr * w / 2, py = cy + Math.sin(an) * rr * h / 2; if (k) g.lineTo(px, py); else g.moveTo(px, py); }
    g.closePath();
  } else if (f === 'check') { g.moveTo(x + w * 0.1, y + h * 0.55); g.lineTo(x + w * 0.4, y + h * 0.9); g.lineTo(x + w * 0.92, y + h * 0.1); }
  else if (f === 'corazon') {
    g.moveTo(x + w / 2, y + h * 0.95); g.bezierCurveTo(x - w * 0.15, y + h * 0.55, x + w * 0.1, y - h * 0.1, x + w / 2, y + h * 0.28);
    g.bezierCurveTo(x + w * 0.9, y - h * 0.1, x + w * 1.15, y + h * 0.55, x + w / 2, y + h * 0.95);
  }
  g.stroke();
}
function trazarLibre(g: CanvasRenderingContext2D, t: Extract<Trazo, { t: 'marcador' | 'resaltador' }>, ancho: number) {
  const p = t.pts.map(q => [q[0] * ancho, q[1]]);
  g.lineCap = 'round'; g.lineJoin = 'round'; g.strokeStyle = t.color; g.fillStyle = t.color; g.lineWidth = t.tam;
  if (p.length === 1) { g.beginPath(); g.arc(p[0][0], p[0][1], t.tam / 2, 0, 6.283); g.fill(); return; }
  g.beginPath(); g.moveTo(p[0][0], p[0][1]);
  for (let i = 1; i < p.length - 1; i++) g.quadraticCurveTo(p[i][0], p[i][1], (p[i][0] + p[i + 1][0]) / 2, (p[i][1] + p[i + 1][1]) / 2);
  g.lineTo(p[p.length - 1][0], p[p.length - 1][1]); g.stroke();
}

// `bloqueado`: hay un menú abierto; ese primer toque solo lo cierra, no deja un punto en la hoja.
interface Props { bloqueado?: boolean; trazos: Trazo[]; herramienta: Herramienta; marcador: Estilo; resaltador: Estilo; figura: Figura; onNuevo: (t: Trazo) => void; onBorrar: (t: Trazo) => void }
export function Dibujo({ bloqueado, trazos, herramienta, marcador, resaltador, figura, onNuevo, onBorrar }: Props) {
  const caja = useRef<HTMLDivElement>(null), cvR = useRef<HTMLCanvasElement>(null), cvT = useRef<HTMLCanvasElement>(null);
  const actual = useRef<Trazo | { borrando: true } | null>(null);
  const [medida, setMedida] = useState({ w: 0, h: 0 });
  const vivo = useRef({ bloqueado, trazos, herramienta, marcador, resaltador, figura, onNuevo, onBorrar, medida });
  vivo.current = { bloqueado, trazos, herramienta, marcador, resaltador, figura, onNuevo, onBorrar, medida };

  useEffect(() => {
    const el = caja.current; if (!el) return;
    const medir = () => { const r = el.getBoundingClientRect(); setMedida(m => (Math.abs(m.w - r.width) < 0.5 && Math.abs(m.h - r.height) < 0.5 ? m : { w: r.width, h: r.height })); };
    medir();
    if (typeof ResizeObserver === 'undefined') { addEventListener('resize', medir); return () => removeEventListener('resize', medir); }
    const ro = new ResizeObserver(medir); ro.observe(el); return () => ro.disconnect();
  }, []);

  const pintar = () => {
    const a = cvR.current, b = cvT.current; if (!a || !b) return;
    const { w, h } = vivo.current.medida, d = Math.min(window.devicePixelRatio || 1, 2);
    for (const cv of [a, b]) { const nw = Math.round(w * d), nh = Math.round(h * d); if (cv.width !== nw) cv.width = nw; if (cv.height !== nh) cv.height = nh; }
    const gR = a.getContext('2d'), gT = b.getContext('2d'); if (!gR || !gT) return;
    gR.setTransform(d, 0, 0, d, 0, 0); gT.setTransform(d, 0, 0, d, 0, 0);
    gR.clearRect(0, 0, w, h); gT.clearRect(0, 0, w, h);
    const dib = (t: Trazo | { borrando: true }) => {
      if ('borrando' in t) return;
      if (t.t === 'resaltador') { gR.globalAlpha = 0.5; trazarLibre(gR, t, w); gR.globalAlpha = 1; } else if (t.t === 'figura') trazarFigura(gT, t, w); else trazarLibre(gT, t, w);
    };
    vivo.current.trazos.forEach(dib); if (actual.current) dib(actual.current);
  };
  useEffect(pintar, [trazos, medida]);

  const punto = (e: PointerEvent): Punto => {
    const r = caja.current!.getBoundingClientRect();
    return [r.width ? (e.clientX - r.left) / r.width : 0, e.clientY - r.top];
  };
  const borrarEn = (p: Punto) => { const v = vivo.current, i = trazoEn(v.trazos, p, v.medida.w); if (i >= 0) v.onBorrar(v.trazos[i]); };
  const alBajar = (e: PointerEvent) => {
    const v = vivo.current; if (v.herramienta === 'escribir' || v.bloqueado) return;
    e.preventDefault(); (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const p = punto(e);
    if (v.herramienta === 'borrador') { actual.current = { borrando: true }; borrarEn(p); return; }
    if (v.herramienta === 'marcador') actual.current = { t: 'marcador', color: v.marcador.color, tam: v.marcador.tam, pts: [p] };
    else if (v.herramienta === 'resaltador') actual.current = { t: 'resaltador', color: v.resaltador.color, tam: v.resaltador.tam, pts: [p] };
    else actual.current = { t: 'figura', f: v.figura, color: v.marcador.color, tam: v.marcador.tam, a: p, b: p };
    pintar();
  };
  const alMover = (e: PointerEvent) => {
    const t = actual.current; if (!t) return;
    const p = punto(e);
    if ('borrando' in t) { borrarEn(p); return; }
    if (t.t === 'figura') t.b = p; else t.pts.push(p);
    pintar();
  };
  const alSoltar = () => {
    const t = actual.current; actual.current = null; if (!t) return;
    const { medida: { w }, onNuevo: nuevo } = vivo.current;
    if (!('borrando' in t)) {
      if (t.t === 'figura') { if (!esToque(t.a, t.b, w)) nuevo({ ...t, a: [Math.round(t.a[0] * 1e4) / 1e4, Math.round(t.a[1] * 10) / 10], b: [Math.round(t.b[0] * 1e4) / 1e4, Math.round(t.b[1] * 10) / 10] }); }
      else nuevo({ ...t, pts: simplificar(t.pts, w) });
    }
    pintar();
  };
  return (
    <div class="td-dibujo" ref={caja} aria-hidden="true">
      <canvas class="td-cv td-cv-r" ref={cvR} />
      <canvas class="td-cv td-cv-t" ref={cvT} />
      <div class={'td-capa' + (herramienta !== 'escribir' ? ' on' : '')} data-herr={herramienta} onPointerDown={alBajar} onPointerMove={alMover} onPointerUp={alSoltar} onPointerCancel={alSoltar} />
    </div>
  );
}
